import React, { useMemo } from 'react';
import JoditEditor from 'jodit-react';
import styled from 'styled-components';

interface ReadOnlyJoditEditorProps {
  content: string;
  height?: number | 'auto';
}

const ReadOnlyJoditEditor: React.FC<ReadOnlyJoditEditorProps> = ({
  content,
  height = 'auto'
}) => {
  const config = useMemo(() => ({
    readonly: true, // 편집 불가능
    toolbar: false, // 툴바 숨김
    statusbar: false, // 상태바 숨김
    height: height,
    language: 'ko',

    // 모든 컨트롤 비활성화
    buttons: [],
    buttonsMD: [],
    buttonsSM: [],
    buttonsXS: [],

    // 한국어 폰트 설정 (에디터와 동일)
    style: {
      background: '#fff',
      color: '#333',
      fontFamily: "'맑은 고딕', 'Malgun Gothic', sans-serif",
      fontSize: '14px',
      lineHeight: '1.6'
    },

    // 불필요한 기능들 비활성화
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    processPasteHTML: false,
    enter: 'p' as const,

    // 이벤트 비활성화
    events: {
      beforeCommand: () => false, // 모든 명령 차단
      beforeSetMode: () => false, // 모드 변경 차단
    }
  }), [height]);

  return (
    <ReadOnlyEditorContainer>
      <JoditEditor
        value={content}
        config={config}
        tabIndex={-1} // 포커스 불가능
      />
    </ReadOnlyEditorContainer>
  );
};

const ReadOnlyEditorContainer = styled.div`
  .jodit-container {
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    min-height: auto !important;
    height: auto !important;
  }

  .jodit-toolbar {
    display: none !important;
  }

  .jodit-status-bar {
    display: none !important;
  }

  .jodit-workplace {
    background: white !important;
    border: none !important;
    min-height: auto !important;
    height: auto !important;
  }

  .jodit-wysiwyg {
    /* 에디터와 완전히 동일한 스타일 */
    font-family: '맑은 고딕', 'Malgun Gothic', sans-serif !important;
    font-size: 14px !important;
    line-height: 1.6 !important;
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
    outline: none !important;
    cursor: default !important; /* 편집 커서 제거 */
    min-height: auto !important;
    height: auto !important;
    overflow: visible !important;

    /* 모든 하위 요소들이 에디터와 동일하게 렌더링되도록 */
    h1, h2, h3, h4, h5, h6 {
      font-family: '맑은 고딕', 'Malgun Gothic', sans-serif !important;
      margin: 16px 0 8px 0 !important;
      font-weight: 600 !important;
    }

    p {
      margin: 8px 0 !important;
    }

    ul, ol {
      margin: 8px 0 !important;
      padding-left: 24px !important;
    }

    blockquote {
      border-left: 4px solid #F68D2E !important;
      border: 1px solid #F68D2E !important;
      margin: 16px 0 !important;
      padding: 8px 16px !important;
      background: white !important;
      font-style: normal !important;
    }

    table {
      border-collapse: collapse !important;
      width: 100% !important;
      margin: 16px 0 !important;

      th, td {
        border: 1px solid #e5e7eb !important;
        padding: 8px 12px !important;
        text-align: left !important;
      }

      th {
        background: #f8fafc !important;
        font-weight: 600 !important;
      }
    }

    img {
      max-width: 100% !important;
      height: auto !important;
      border-radius: 4px !important;
      margin: 8px 0 !important;
    }

    a {
      color: #667eea !important;
      text-decoration: none !important;

      &:hover {
        text-decoration: underline !important;
      }
    }

    code {
      background: #f1f5f9 !important;
      padding: 2px 4px !important;
      border-radius: 3px !important;
      font-family: 'Courier New', monospace !important;
      font-size: 0.9em !important;
    }

    pre {
      background: #f1f5f9 !important;
      padding: 12px !important;
      border-radius: 6px !important;
      overflow-x: auto !important;
      margin: 16px 0 !important;

      code {
        background: none !important;
        padding: 0 !important;
      }
    }
  }

  /* 포커스 상태 제거 */
  .jodit-container:focus-within {
    border: none !important;
    box-shadow: none !important;
  }

  /* 호버 효과 제거 */
  .jodit-wysiwyg:hover {
    background: white !important;
  }
`;

export default ReadOnlyJoditEditor;